import React from "react";
//#region types
import {
  BaseFormFieldProps,
  CurrencyInputProps,
  NumberInputProps,
  SelectInputProps,
} from "models/propTypes/wintFormPropsTypes";
import { RutRotFieldGroupProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { InvoiceFormTypes } from "models/formTypes";
import { WintFormKey } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  CurrencyInput,
  VerticalFormField,
  FormFieldLayout,
  SelectInput,
  NumberInput,
} from "components/common/wintForms";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { getIn } from "formik";
import cssExports from "theme/styles/main.module.scss";

const RutRotFieldGroup: React.FC<RutRotFieldGroupProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { connect, values, errors, touched, rutRotWorkTypes, index } = props;
  const rowName = `rutRot.rutRotArticles[${index}]` as WintFormKey<InvoiceFormTypes.Create>;

  const setRowField = (name: string, value: any, shouldValidate?: boolean) => {
    connect(rowName).setValue({ ...props.row, [name]: value }, shouldValidate);
  };

  const handleChange = (name: string) => (e: React.ChangeEvent<any>) => {
    setRowField(name, e.target.value);
  };

  const handleBlur = (name: string) => (e: React.FocusEvent<any>) => {
    const connector = connect(`${rowName}.${name}` as WintFormKey<InvoiceFormTypes.Create>);
    connector.onBlur(e);
    connector.validate();
  };

  const connectRowToForm = (name: keyof InvoiceFormTypes.RutRotArticleItem): BaseFormFieldProps => {
    const fullName = `${rowName}.${name}`;
    const error = getIn(errors, fullName);
    const hasBeenTouched = !!getIn(touched, fullName);

    return {
      name: fullName,
      value: props.row[name],
      onChange: handleChange(name),
      onBlur: handleBlur(name),
      setValue: (value: any, shouldValidate?: boolean) => setRowField(name, value, shouldValidate),
      setTouched: (isTouched?: boolean, shouldValidate?: boolean) =>
        connect(fullName as WintFormKey<InvoiceFormTypes.Create>).setTouched(isTouched, shouldValidate),
      touched: hasBeenTouched,
      error: error,
      hasError: hasBeenTouched && !!error,
    };
  };

  const onMaterialCostChange = (e: React.ChangeEvent<any>) => {
    const updatedValue = e.target.value;

    let isCostEqual = true;
    const newRutRotArticles = values.rutRot?.rutRotArticles?.map((r, index) => {
      if (index === props.index) {
        const product = values.products?.find((p) => p.uniqueId === r.materialCostSuggestionsId);
        const cost = invoiceService.calculateAmount(product?.price ?? 0, product?.quantity);

        isCostEqual = (cost ?? 0) === Number(updatedValue);

        return { ...r, materialCost: updatedValue, materialCostSuggestionsId: undefined };
      } else {
        return r;
      }
    });
    const otherCost = invoiceService.calculateRutRotOtherCost(values.products ?? [], newRutRotArticles ?? []);

    setRowField("materialCost", e.target.value);
    connect("rutRot.houseWorkOtherCosts").setValue(otherCost);
    if (!isCostEqual) {
      connect("rutRot.rutRotArticles").setValue(newRutRotArticles);
    }
  };

  const onMaterialCostSuggestionChange = (itemId?: number) => {
    const newValues = [...(values.rutRot?.rutRotArticles ?? [])];

    if (itemId) {
      const product = values.products?.find((p) => p.uniqueId === itemId);
      const cost = invoiceService.calculateAmount(product?.price ?? 0, product?.quantity);
      newValues[props.index] = {
        ...newValues[props.index],
        materialCost: cost,
        materialCostSuggestionsId: itemId,
      };
    } else {
      newValues[props.index] = {
        ...newValues[props.index],
        materialCost: 0,
        materialCostSuggestionsId: undefined,
      };
    }
    connect("rutRot.rutRotArticles").setValue(newValues);
  };

  return (
    <FormFieldLayout label={props.row["name"]} direction="horizontal" className={cssExports.labelRightAlign}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={5}>
          <VerticalFormField<NumberInputProps>
            Component={NumberInput}
            {...connectRowToForm("noOfHours")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.NumberOfHours)}
            noOuterSpacing
            decimalPoints={2}
          />
        </Grid>
        <Grid item xs={7}>
          <VerticalFormField<SelectInputProps>
            Component={SelectInput}
            {...connectRowToForm("typeOfWorkId")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.TypeOfWork)}
            items={rutRotWorkTypes}
            noOuterSpacing
          />
        </Grid>
        <Grid item xs={12}>
          <VerticalFormField<CurrencyInputProps>
            Component={CurrencyInput}
            {...connectRowToForm("materialCost")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MaterialCost)}
            onChange={onMaterialCostChange}
            currency={values.currency}
            noOuterSpacing
          />
        </Grid>
        <Grid item xs={8}>
          <VerticalFormField<SelectInputProps>
            Component={SelectInput}
            {...connectRowToForm("materialCostSuggestionsId")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MaterialCostSuggestions)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.MaterialCostSuggestionsDescription
            )}
            items={
              values.products
                ?.filter((p) => values.rutRot?.rutRotArticles?.every((u) => u.id !== p.uniqueId) && p.text)
                .map((v) => ({ value: v.uniqueId!, label: v.text! })) ?? []
            }
            setValue={onMaterialCostSuggestionChange}
            className={cssExports.compactField}
            noOuterSpacing
          />
        </Grid>
      </Grid>
    </FormFieldLayout>
  );
};

export default RutRotFieldGroup;
